/**
 * @license
 * Copyright 2018 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @file Support for rendering point annotations.
 */

import { AnnotationType, CellShape } from 'neuroglancer/annotation';
import { AnnotationRenderContext, AnnotationRenderHelper, AnnotationShaderGetter, registerAnnotationTypeRenderHandler } from 'neuroglancer/annotation/type_handler';
import { ShaderBuilder, ShaderProgram } from 'neuroglancer/webgl/shader';
import { defineVectorArrayVertexShaderInput } from 'neuroglancer/webgl/shader_lib';
import { defineVertexId, VertexIdHelper } from 'neuroglancer/webgl/vertex_id';
import {SegmentColorShaderManager, SegmentStatedColorShaderManager} from 'neuroglancer/segment_color';
import {GPUHashTable, HashSetShaderManager} from 'neuroglancer/gpu_hash/shader';
import {HashMapUint64} from 'neuroglancer/gpu_hash/hash_table';
import {allToolsButton, viewer} from "../../main";

class RenderHelper extends AnnotationRenderHelper {
  visibleSegments: any
  gpuHashTable: any
  colorHash: any
  segmentStatedColors: any
  protected segmentStatedColorShaderManager =
    new SegmentStatedColorShaderManager('segmentStatedColor');
  protected segmentColorShaderManager = new SegmentColorShaderManager('segmentColorHash');
  private gpuSegmentStatedColorHashTable: GPUHashTable<HashMapUint64>|undefined;
  private hashTableManager = new HashSetShaderManager('visibleSegments');

  private defineShaderCommon(builder: ShaderBuilder) {
    const { rank } = this;
    // Position of point in model coordinates.
    defineVectorArrayVertexShaderInput(
      builder, 'float', WebGL2RenderingContext.FLOAT, /*normalized=*/ false, 'VertexPosition',
      rank);
    // Position offset Array of point in model coordinates.
    defineVectorArrayVertexShaderInput(
      builder, 'float', WebGL2RenderingContext.FLOAT, /*normalized=*/ false, 'VertexOffsetArr',
      16);

    defineVectorArrayVertexShaderInput(
      builder, 'float', WebGL2RenderingContext.FLOAT, /*normalized=*/ false, 'ElementIndex',
      16);

    this.hashTableManager.defineShader(builder);
    this.segmentColorShaderManager.defineShader(builder);
    builder.addVertexCode(`
      float cellArea;

      void setCellShapeColor(vec4 color) {
        vColor = color;
      }
      float uintToFloat(highp uint uintNum){
          uint flag = uintNum >> 15;
          float result;
          float num = step(1.0,float(flag)) * -65536.0;
          result = float(uintNum) + num;
          return result;
      }
      //两个16bits int打包为32 float后，解析为二维浮点数向量
      vec2 floatBitsToVec2(float floatBits){
          vec2 result;
          result.x = uintToFloat(floatBitsToUint(floatBits) & 0x0000FFFFu);
          result.y = uintToFloat((floatBitsToUint(floatBits) & 0xFFFF0000u) >> 16u);
          return result;
      }
      //解析顶点索引，获取细胞轮廓偏移量
      vec3 getBorderLineVertexOffset(int index){
          float vertexOffsetArr[16] = getVertexOffsetArr0();
          return vec3(floatBitsToVec2(vertexOffsetArr[index]), 0);
      }
      //获取顶点索引值
      int getIndexInBorderArr(int offset){
        float vertexIndex[16] = getElementIndex0();
        int instanceVertex = offset;
        int u32ArrIndex = instanceVertex / 8;
        int offsetInUint32 = instanceVertex % 8 * 4;
        uint result = 0xfu & (floatBitsToUint(vertexIndex[u32ArrIndex]) >> offsetInUint32);

        return int(result);
      }
      //通过顶点索引，获取细胞轮廓偏移量
      vec3 getIndexedVertexOffset(int offset){
        int index = getIndexInBorderArr(gl_VertexID + offset);
        float vertexOffsetArr[16] = getVertexOffsetArr0();

        return vec3(floatBitsToVec2(vertexOffsetArr[index]), 0.0);
      }
      // 获取细胞面积
      void getCellArea(uint cellType) {
        cellArea = float(cellType);
      }
      // 设置聚类ID Varying，作为颜色hashmap的key获取聚类颜色
      void setClusterIDVarying(highp uint cellType) {
        vClusterId = cellType;
      }
      // 设置细胞点选状态 Varying，由于细胞轮廓可能将一个细胞切分成两个来渲染，两个细胞的cellID是一样的，所以需要将cellId与uSelectedIndex进行比较，将两个细胞都高亮
      void setCellIdVarying(uint cellId) {
        if(uSelectedIndex == cellId){
          vCellPickState = 1;
        }else{
          vCellPickState = 0;
        }
      }
    `);

    builder.addVertexMain(`
      float modelPosition[${rank}] = getVertexPosition0();
      float clipCoefficient = getSubspaceClipCoefficient(modelPosition);
      if (clipCoefficient == 0.0) {
        gl_Position = vec4(2.0, 0.0, 0.0, 1.0);
        return;
      }
      ${this.invokeUserMain}
      ${this.setPartIndex(builder)};
    `);
  }

  //细胞轮廓目前只适配2D，如需适配3D，需要在makeShaderGetter3d添加逻辑
  private makeShaderGetter2d = (extraDim: number, hasMapColor?: boolean) =>
    this.getDependentShader(`annotation/cellShape:2d:${extraDim}:${hasMapColor}`, (builder: ShaderBuilder) => {
      defineVertexId(builder);
      this.defineShaderCommon(builder);

      builder.addUniform('highp int', 'uCellFaceDrawState');
      builder.addUniform('highp int', 'uLODFirstIndex');
      builder.addUniform('highp float', 'uLODScale');
      builder.addVarying('highp uint', 'vClusterId', 'flat');
      //细胞轮廓hover状态，1:hover状态;0：非hover状态
      builder.addVarying('int', 'vCellPickState', 'flat');
      //判断细胞是否属于切割成两个渲染的细胞
      builder.addVarying('int', 'vIsSplitCellState', 'flat');
      builder.addUniform('highp uint', 'uShowAllSegments');

      builder.addUniform('highp uint', 'uFilterByCellType');
      if (!hasMapColor) {//非聚类情况下，就是cellbin gene heatMap,添加比色卡uniform、根据比色卡配置渲染颜色
        builder.addUniform('highp vec3', 'uColorBarRGBList', 10);
        builder.addUniform('highp int', 'uColorBarMax');
        builder.addUniform('highp int', 'uColorBarMin');
        builder.addVertexMain(`
          vColor.rgb = colormapCustom(int(prop_expCount()),uColorBarMin,uColorBarMax,uColorBarRGBList);`
        )
      } else {
        builder.addVertexMain('vClusterId = prop_clusterID();\n        if(uFilterByCellType > 0u){\n          vClusterId = prop_cellTypeID();\n        }')
      }
      builder.addVertexMain(`
        vec3 offsetVec;
        //细胞是否属于切割成两个渲染的细胞，存在66号位置中，0为非切割的细胞，1为切割的细胞
        vIsSplitCellState = getIndexInBorderArr(66);
        float factorPow = float(getIndexInBorderArr(67));
        if (uCellFaceDrawState == 0) {
          offsetVec = vec3(0.0, 0.0, 0.0);
          gl_PointSize = uLODScale * sqrt(cellArea) * 0.8;//0.8是经验值，用正方形的点表示对应面积的不规则细胞时视觉差异较小
        } else {
          offsetVec = getIndexedVertexOffset(uLODFirstIndex) * (1.0 + float(uCellFaceDrawState - 1)/20.0);
        }
        modelPosition[0] += offsetVec.x * pow(0.1,factorPow);
        modelPosition[1] += offsetVec.y * pow(0.1,factorPow);
        
        vec3 tempPosition = projectModelVectorToSubspace(modelPosition);
       
        vec4 vertexClip = uModelViewProjection * vec4(tempPosition, 1.0);
        vec3 vertexDevice = vertexClip.xyz / vertexClip.w;
        
        gl_Position = vec4(vertexDevice, 1.0);
      `);

      let getMappedIdColor = `vec3 getMappedIdColor(uint64_t value) {
          `;
      // If the value has a mapped color, use it; otherwise, compute the color.
      if (hasMapColor) {
        this.segmentStatedColorShaderManager.defineShader(builder);
        getMappedIdColor += `
              vec3 rgb;
              if (${this.segmentStatedColorShaderManager.getFunctionName}(value, rgb)) {
                return rgb;
              } else {
                return segmentColorHash(value);
              }
            `;
      } else {
        getMappedIdColor += `  return segmentColorHash(value);
          `;
      }
      getMappedIdColor += `
      }
      `;
      builder.addFragmentCode(getMappedIdColor);

      let fragmentMianStr:string;
      if (hasMapColor) {//有颜色映射意味着有cluster信息，是聚类图，没有cluster是基因热图
        fragmentMianStr = `
        uint64_t clusterKey;
        clusterKey.value[0] = vClusterId;
        clusterKey.value[1] = 0u;
        bool has = uShowAllSegments != 0u ? true : ${this.hashTableManager.hasFunctionName}(clusterKey);
        if (!has) {
          discard;
        }
        vec3 rgb = getMappedIdColor(clusterKey);

        if(vCellPickState > 0) {
          emitAnnotation(vec4(mix(rgb.rgb, vec3(1.0,1.0,1.0), 0.75), oFactor));
        }else{
          emitAnnotation(vec4(rgb, oFactor));
        }`
      } else {
        fragmentMianStr = `
        
        if(vCellPickState > 0) {
          emitAnnotation(vec4(mix(vColor.rgb, vec3(1.0,1.0,1.0), 0.75), oFactor));
        }else{
          emitAnnotation(vec4(vColor.rgb, oFactor));
        }`
      }
      builder.setFragmentMain(fragmentMianStr);
    });

  private shaderGetter1d = this.makeShaderGetter2d(1);

  //用线绘制细胞边缘的shader
  private makeCellLinesShaderGetter2d = (extraDim: number, hasMapColor?: boolean) =>
    this.getDependentShader(`annotation/cellShapeLines:2d:${extraDim}:${hasMapColor}`, (builder: ShaderBuilder) => {
      defineVertexId(builder);
      this.defineShaderCommon(builder);

      builder.addUniform('highp int', 'uCellOutlineDrawState');
      builder.addVarying('highp uint', 'vClusterId', 'flat');
      //细胞轮廓hover状态，1:hover状态;0：非hover状态
      builder.addVarying('int', 'vCellPickState', 'flat');
      //判断细胞是否属于切割成两个渲染的细胞
      builder.addVarying('int', 'vIsSplitCellState', 'flat');
      builder.addUniform('highp uint', 'uShowAllSegments');
      builder.addUniform('int', 'uIsCustomColor');
      builder.addUniform('vec3', 'uCustomColor');
      builder.addUniform('highp float', 'uLODScale');
      builder.addUniform('highp uint', 'uFilterByCellType');
      if (hasMapColor){
        builder.addVertexMain('vClusterId = prop_clusterID();\n        if(uFilterByCellType > 0u){\n          vClusterId = prop_cellTypeID();\n        }')
      }
      builder.addVertexMain(`
        vec3 offsetVec;
        //细胞是否属于切割成两个渲染的细胞，存在66号位置中，0为非切割的细胞，1为切割的细胞
        vIsSplitCellState = getIndexInBorderArr(66);
        // vIsSplitCellState = 0;
        
        float factorPow = float(getIndexInBorderArr(67));
        int computedIndex = min((uCellOutlineDrawState * gl_VertexID ), 16);
        if(computedIndex > 15){
          if(vIsSplitCellState > 0){
            offsetVec = getBorderLineVertexOffset(15);
          }else{
            offsetVec = getBorderLineVertexOffset(0);
          }
        }else{
          offsetVec = getBorderLineVertexOffset(computedIndex) * (1.0 + float(uCellOutlineDrawState -1)/10.0);
        }
        modelPosition[0] += offsetVec.x * pow(0.1,factorPow);
        modelPosition[1] += offsetVec.y * pow(0.1,factorPow);
        
        vec3 tempPosition = projectModelVectorToSubspace(modelPosition);
       
        vec4 vertexClip = uModelViewProjection * vec4(tempPosition, 1.0);
        vec3 vertexDevice = vertexClip.xyz / vertexClip.w;
        
        gl_Position = vec4(vertexDevice, 1.0);
      `);

      let getMappedIdColor = `vec3 getMappedIdColor(uint64_t value) {
          `;
      // If the value has a mapped color, use it; otherwise, compute the color.
      if (hasMapColor) {
        this.segmentStatedColorShaderManager.defineShader(builder);
        getMappedIdColor += `
              vec3 rgb;
              if (${this.segmentStatedColorShaderManager.getFunctionName}(value, rgb)) {
                return rgb;
              } else {
                return segmentColorHash(value);
              }
            `;
      } else {
        getMappedIdColor += `  return segmentColorHash(value);
          `;
      }
      getMappedIdColor += `
      }
      `;
      builder.addFragmentCode(getMappedIdColor);

      builder.setFragmentMain(`
      uint64_t clusterKey;
      clusterKey.value[0] = vClusterId;
      clusterKey.value[1] = 0u;
      bool has = uShowAllSegments != 0u ? true : ${this.hashTableManager.hasFunctionName}(clusterKey);
      if (!has) {
        discard;
      }
      vec3 rgb = getMappedIdColor(clusterKey);
      if(vCellPickState > 0) {
        emitAnnotation(vec4(mix(rgb.rgb, vec3(1.0,1.0,1.0), 0.75), oFactor));
      }else if(uIsCustomColor == 0){
        emitAnnotation(vec4(rgb, oFactor));
      }else{
        emitAnnotation(vec4(vec3(uCustomColor), oFactor));
      }
      
    `);
    });


  private shaderLinesGetter2dHasMapColor = this.makeCellLinesShaderGetter2d(2, true);

  private shaderLinesGetter2dHasNoMapColor = this.makeCellLinesShaderGetter2d(2, false);

  private vertexIdHelper = this.registerDisposer(VertexIdHelper.get(this.gl));

  enable(
    shaderGetter: AnnotationShaderGetter, context: AnnotationRenderContext,
    callback: (shader: ShaderProgram) => void) {
    super.enable(shaderGetter, context, shader => {
      const binder = shader.vertexShaderInputBinders['VertexPosition'];
      binder.enable(1);
      this.gl.bindBuffer(WebGL2RenderingContext.ARRAY_BUFFER, context.buffer.buffer);
      binder.bind(this.serializedBytesPerAnnotation, context.bufferOffset);

      const { vertexIdHelper } = this;
      vertexIdHelper.enable();
      let binder1,binder2;

      if (context.extendBuffer) {
        binder1 = shader.vertexShaderInputBinders['VertexOffsetArr'];
        binder1.enable(1);
        this.gl.bindBuffer(WebGL2RenderingContext.ARRAY_BUFFER, context.extendBuffer.buffer);
        //64，一个实例所占用的实例化数组byte数，很重要
        binder1.bind(64, context.bufferOffset);
      }

      if (context.elementIndexBuffer) {
        binder2 = shader.vertexShaderInputBinders['ElementIndex'];
        binder2.enable(1);
        this.gl.bindBuffer(WebGL2RenderingContext.ARRAY_BUFFER, context.elementIndexBuffer.buffer);
        //64，一个实例所占用的实例化数组byte数，很重要。这里一个实例，占用16个float，一共64个字节
        binder2.bind(64, context.bufferOffset);
      }

      callback(shader);
      vertexIdHelper.disable();
      binder.disable();
      if(binder1){
        binder1.disable();
      }
      if(binder2){
        binder2.disable();
      }
    });
  }

  draw(context: AnnotationRenderContext) {
    const { numChunkDisplayDims } = context.chunkDisplayTransform;
    // const {segmentationStates, se} = context.renderContext.drawSegmentContext
    let curFilterByCellType = false;//细胞过滤是否通过细胞类型过滤，默认是通过细胞聚类cluster过滤
    if (context.renderContext.drawSegmentContext) {
      const {segmentColorHash: {value: colorHash},  segmentStatedColors:{value: segmentStatedColors}, segmentationGroupState: {value: {visibleSegments: visibleSegments,filterByCellType}}} = context.renderContext.drawSegmentContext
      this.gpuHashTable = this.registerDisposer(
        GPUHashTable.get(this.gl, visibleSegments.hashTable));
      this.colorHash =colorHash
      this.segmentStatedColors = segmentStatedColors
      this.visibleSegments = visibleSegments
      curFilterByCellType = filterByCellType
    }
    const isCluster = context.renderContext.isCluster;
    const hasMapColor = isCluster && this.segmentStatedColors
    switch (numChunkDisplayDims) {
      case 3:
        //3d 细胞轮廓功能待开发
        break;
      case 2:
      case 1:

        const scaleFraction = 1/viewer.crossSectionScale.value;
        if (viewer.cellShowMode.value === 'filled' || viewer.cellShowMode.value === 'both' || !isCluster) {//context.renderContext.isCluster作为gene heatmap的标志，false为heatmap 默认只填充
          this.enable(
            numChunkDisplayDims === 2 ? this.makeShaderGetter2d(2, hasMapColor) : this.shaderGetter1d, context,
            shader => {
              const {gl} = shader;

              if (hasMapColor){//聚类，使用聚类颜色渲染
                // 离散颜色
                if (this.segmentStatedColors) {
                  let {gpuSegmentStatedColorHashTable} = this;
                  if (gpuSegmentStatedColorHashTable === undefined ||
                    gpuSegmentStatedColorHashTable.hashTable !== this.segmentStatedColors.hashTable) {
                    gpuSegmentStatedColorHashTable?.dispose();
                    this.gpuSegmentStatedColorHashTable = gpuSegmentStatedColorHashTable =
                      GPUHashTable.get(gl, this.segmentStatedColors.hashTable);
                  }
                  this.segmentStatedColorShaderManager.enable(gl, shader, gpuSegmentStatedColorHashTable);
                  // 默认颜色
                  this.segmentColorShaderManager.enable(gl, shader, this.colorHash || 0);
                }

                // visibleSegment
                if (this.gpuHashTable) {
                  this.hashTableManager.enable(
                    this.gl, shader, this.gpuHashTable);
                }

                gl.uniform1ui(
                  shader.uniform('uShowAllSegments'),
                  this.visibleSegments && this.visibleSegments.hashTable.size ? 0 : 1);
                gl.uniform1ui(shader.uniform('uFilterByCellType'), curFilterByCellType ? 1 : 0);

              } else {//gene heatMap 设置比色卡uniform、根据比色卡配置渲染颜色
                gl.uniform3fv(shader.uniform('uColorBarRGBList'), new Float32Array(viewer.allDataSource.colorscale.colorData));
                gl.uniform1i(shader.uniform('uColorBarMax'), viewer.color_scale[allToolsButton.genelistFile].maxValue );
                gl.uniform1i(shader.uniform('uColorBarMin'), viewer.color_scale[allToolsButton.genelistFile].minValue );
              }

              gl.uniform1f(shader.uniform('uLODScale'), scaleFraction);
              gl.uniform1i(shader.uniform('uCellFaceDrawState'), 1);

              if(!viewer.isMoving || scaleFraction > 0.6){
                if (scaleFraction < 0.1) {

                  gl.uniform1i(shader.uniform('uCellFaceDrawState'), 0);
                  gl.drawArraysInstanced(WebGL2RenderingContext.POINTS, 0, 1, context.cellShapeCount);
                }else if (scaleFraction < 0.2) {
                  gl.uniform1i(shader.uniform('uLODFirstIndex'), 42);

                  gl.drawArraysInstanced(WebGL2RenderingContext.TRIANGLES, 0, 18, context.cellShapeCount);
                } else {
                  gl.uniform1i(shader.uniform('uLODFirstIndex'), 0);
                  gl.drawArraysInstanced(WebGL2RenderingContext.TRIANGLES, 0, 42, context.cellShapeCount);
                }
              } else if (scaleFraction < 0.1) {

                gl.uniform1i(shader.uniform('uCellFaceDrawState'), 0);

                gl.drawArraysInstanced(WebGL2RenderingContext.POINTS, 0, 1, context.cellShapeCount);
              } else if (scaleFraction < 0.2) {
                gl.uniform1i(shader.uniform('uCellFaceDrawState'), 3);
                gl.uniform1i(shader.uniform('uLODFirstIndex'), 60);

                gl.drawArraysInstanced(WebGL2RenderingContext.TRIANGLES, 0, 6, context.cellShapeCount);
              } else if (scaleFraction <= 0.6) {
                gl.uniform1i(shader.uniform('uLODFirstIndex'), 42);

                gl.drawArraysInstanced(WebGL2RenderingContext.TRIANGLES, 0, 18, context.cellShapeCount);
              }

            }
          );
        }
        //使用线来绘制细胞边缘
        if ((viewer.cellShowMode.value === 'both' || viewer.cellShowMode.value === 'outlined') && isCluster) {//context.renderContext.isCluster作为gene heatmap的标志，false为heatmap 默认只填充所以不绘制外轮廓线
          this.enable(
            numChunkDisplayDims === 2 ? (hasMapColor ? this.shaderLinesGetter2dHasMapColor : this.shaderLinesGetter2dHasNoMapColor) : this.shaderGetter1d, context,
            shader => {
              const { gl } = shader;
              // 离散颜色
              if (this.segmentStatedColors) {
                let {gpuSegmentStatedColorHashTable} = this;
                if (gpuSegmentStatedColorHashTable === undefined ||
                  gpuSegmentStatedColorHashTable.hashTable !== this.segmentStatedColors.hashTable) {
                  gpuSegmentStatedColorHashTable?.dispose();
                  this.gpuSegmentStatedColorHashTable = gpuSegmentStatedColorHashTable =
                    GPUHashTable.get(gl,this.segmentStatedColors.hashTable);
                }
                this.segmentStatedColorShaderManager.enable(gl, shader, gpuSegmentStatedColorHashTable);
                // 默认颜色
                this.segmentColorShaderManager.enable(gl, shader, this.colorHash || 0);
              }
              // visibleSegment
              if (this.gpuHashTable) {
                this.hashTableManager.enable(
                  this.gl, shader,this.gpuHashTable);
              }

              gl.uniform1f(shader.uniform('uLODScale'), scaleFraction);
              //'White', 'Black', 'Green'
              gl.uniform1i(shader.uniform('uIsCustomColor'), 1);
              switch (viewer.outLineColor.value) {
                case 'Cluster':
                  gl.uniform1i(shader.uniform('uIsCustomColor'), 0);
                  break;
                case 'White':
                  gl.uniform3f(shader.uniform('uCustomColor'), 1, 1, 1);
                  break;
                case 'Black':
                  gl.uniform3f(shader.uniform('uCustomColor'), 0, 0, 0);
                  break;
                case 'Green':
                  gl.uniform3f(shader.uniform('uCustomColor'), 0, 1, 0);
                  break;
              }
              gl.uniform1ui(
                shader.uniform('uShowAllSegments'),
                this.visibleSegments && this.visibleSegments.hashTable.size ? 0 : 1);

              gl.uniform1ui(shader.uniform('uFilterByCellType'), curFilterByCellType ? 1 : 0);
              gl.uniform1i(shader.uniform('uCellOutlineDrawState'), 1);
              if(!viewer.isMoving || scaleFraction > 0.3){

                gl.drawArraysInstanced(WebGL2RenderingContext.LINE_STRIP, 0, 17, context.cellShapeCount);
              } else if (scaleFraction <0.1) {
                gl.uniform1i(shader.uniform('uCellOutlineDrawState'), 3);
                gl.drawArraysInstanced(WebGL2RenderingContext.LINE_STRIP, 0, 5, context.cellShapeCount);
              } else if (scaleFraction < 0.3) {
                gl.uniform1i(shader.uniform('uCellOutlineDrawState'), 2);
                gl.drawArraysInstanced(WebGL2RenderingContext.LINE_STRIP, 0, 9, context.cellShapeCount);
              }
            }
          );
        }
        break;
    }
  }
}

registerAnnotationTypeRenderHandler<CellShape>(AnnotationType.CELL_SHAPE, {
  sliceViewRenderHelper: RenderHelper,
  perspectiveViewRenderHelper: RenderHelper,
  defineShaderNoOpSetters(builder) {
    builder.addVertexCode(`
      void setCellShapeColor(vec4 color) {}
    `);
  },
  pickIdsPerInstance: 1,
  snapPosition(position, data, offset) {
    position.set(new Float32Array(data, offset, position.length));
  },
  getRepresentativePoint(out, ann) {
    out.set(ann.cellShape);
  },
  updateViaRepresentativePoint(oldAnnotation, position) {
    return { ...oldAnnotation, cellShape: new Float32Array(position) };
  }
});
